import { useEffect, useState } from "react";
import type { SetupProgress, SetupTab } from "@voice/api-contract";
import { boundedStatusRequest } from "./ServiceStatus";

export function SetupChecklistContent({
  progress,
  onTab,
}: {
  progress: SetupProgress;
  onTab: (tab: SetupTab) => void;
}) {
  const incomplete = progress.checks.filter((check) => !check.ready);
  return (
    <>
      <p>
        {incomplete.length
          ? `${progress.checks.length - incomplete.length} of ${progress.checks.length} record checks complete.`
          : progress.nextStep === null ? "Setup checks and staff calling confirmation are recorded for this configuration."
          : "All record checks pass. Record the actual calling test in Orders before marking setup complete."}
      </p>
      <ul className="cr-service-status-list">
        {incomplete.map((check) => (
          <li key={check.key}>
            <div>
              <strong>{check.title}</strong>
              <ul>
                {check.reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
              <button
                className="cr-text-button"
                onClick={() => onTab(check.tab)}
              >
                Open {check.tab === "overview" ? "account details" : check.tab}
              </button>
            </div>
          </li>
        ))}
      </ul>
      <p className="cr-form-note">
        These are saved-record checks, not live calling tests. Pending
        Activation does not mean a working phone is switched off. Account status
        is changed separately by staff.
      </p>
    </>
  );
}

export function SetupChecklist({
  api,
  accountId,
  refreshVersion,
  onTab,
}: {
  api: <T>(path: string) => Promise<T>;
  accountId: string;
  refreshVersion: number;
  onTab: (tab: SetupTab) => void;
}) {
  const [progress, setProgress] = useState<SetupProgress | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    setProgress(null);
    setError(false);
    void boundedStatusRequest(
      api<SetupProgress>(`/admin/accounts/${accountId}/onboarding`),
    )
      .then((value) => {
        if (alive) setProgress(value);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [api, accountId, refreshVersion, attempt]);
  if (error)
    return (
      <div role="alert">
        <p>Setup requirements could not load. No account changes were made.</p>
        <button
          className="cr-button secondary"
          onClick={() => setAttempt((value) => value + 1)}
        >
          Retry setup checks
        </button>
      </div>
    );
  if (!progress) return <p role="status">Reading setup requirements…</p>;
  return <SetupChecklistContent progress={progress} onTab={onTab} />;
}
